// IndexedDB cache for converted HEIC images
// Stores converted JPEGs to avoid re-conversion on subsequent renders

const DB_NAME = 'heic-cache';
const STORE_NAME = 'converted-images';
const DB_VERSION = 1;
const CACHE_EXPIRY_DAYS = 7;

interface CachedImage {
	key: string;
	blob: Blob;
	timestamp: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
	if (dbPromise) return dbPromise;

	dbPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);

		request.onerror = () => reject(request.error);

		request.onsuccess = () => resolve(request.result);

		request.onupgradeneeded = (event) => {
			const db = (event.target as IDBOpenDBRequest).result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
				store.createIndex('timestamp', 'timestamp', { unique: false });
			}
		};
	}).catch((err) => {
		dbPromise = null;
		throw err;
	});

	return dbPromise;
};

export const getCachedImage = async (key: string): Promise<Blob | null> => {
	try {
		const db = await openDB();
		return new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readonly');
			const store = transaction.objectStore(STORE_NAME);
			const request = store.get(key);

			request.onsuccess = () => {
				const result = request.result as CachedImage | undefined;
				if (!result) {
					resolve(null);
					return;
				}

				// Check if cache is expired
				const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
				if (Date.now() - result.timestamp > expiryTime) {
					// Cache expired, delete it
					deleteFromCache(key).catch(console.error);
					resolve(null);
					return;
				}

				resolve(result.blob);
			};

			request.onerror = () => reject(request.error);
		});
	} catch (error) {
		console.warn('Failed to get cached image:', error);
		return null;
	}
};

export const setCachedImage = async (key: string, blob: Blob): Promise<void> => {
	try {
		const db = await openDB();
		return new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);

			const cachedImage: CachedImage = {
				key,
				blob,
				timestamp: Date.now(),
			};

			const request = store.put(cachedImage);

			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	} catch (error) {
		console.warn('Failed to cache image:', error);
	}
};

const deleteFromCache = async (key: string): Promise<void> => {
	try {
		const db = await openDB();
		return new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);
			const request = store.delete(key);

			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	} catch (error) {
		console.warn('Failed to delete from cache:', error);
	}
};

// Clean up expired cache entries (call periodically)
export const cleanupExpiredCache = async (): Promise<void> => {
	try {
		const db = await openDB();
		const expiryTime = Date.now() - CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

		return new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);
			const index = store.index('timestamp');
			const range = IDBKeyRange.upperBound(expiryTime);

			const request = index.openCursor(range);

			request.onsuccess = (event) => {
				const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
				if (cursor) {
					cursor.delete();
					cursor.continue();
				} else {
					resolve();
				}
			};

			request.onerror = () => reject(request.error);
		});
	} catch (error) {
		console.warn('Failed to cleanup cache:', error);
	}
};

// Generate a cache key from the photo string
export const getCacheKey = (photoString: string): string => {
	return `heic-${photoString}`;
};
